import {
  opportunitiesProductsListOptions,
  opportunitiesRetrieveOptions,
  ReturnOpportunityProductReadable
} from '@crm/api'
import { calculateDiscount, composePrice, getProductsSummary } from '@crm/utils'
import {
  ConditionalText,
  DayLabel,
  TableActions,
  TableContainer,
  Text
} from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Flex, MenuProps, Table, TableColumnsType, Tooltip } from 'antd'
import { Edit2, Trash } from 'iconsax-react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AddEditProduct } from './AddEditProduct'
import { DeleteProduct } from './DeleteProduct'

import { ProductsHeader } from './ProductsHeader'
import { ActionsType } from './types'

type ModalType = 'add' | 'edit' | 'delete'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  add: AddEditProduct,
  edit: AddEditProduct,
  delete: DeleteProduct
}

export const ProductsCol = () => {
  const { t } = useLocales()
  const { opportunityId } = useParams()
  const { params } = useQueryUpdate()

  const [modalState, setModalState] = useState<{
    type: ModalType | null
    product: ReturnOpportunityProductReadable | null
  }>({ product: null, type: null })

  const { data: opportunityData, isLoading: isOpportunityLoading } = useQuery({
    ...opportunitiesRetrieveOptions({ path: { id: Number(opportunityId) } }),
    enabled: !!opportunityId
  })

  const { data: opportunityProducts, isLoading: isProductsLoading } = useQuery({
    ...opportunitiesProductsListOptions({
      query: { opportunity: Number(opportunityId), ...params }
    }),
    enabled: !!opportunityId
  })

  const opportunityCurrency = opportunityData?.currency?.symbol
  const isLoading = isProductsLoading || isOpportunityLoading

  const summaryData = useMemo(() => {
    if (!opportunityProducts?.results?.length) return null
    return getProductsSummary(opportunityProducts.results)
  }, [opportunityProducts])

  const actionItems = (
    product: ReturnOpportunityProductReadable
  ): MenuProps['items'] => [
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => setModalState({ product, type: 'edit' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ product, type: 'delete' })
    }
  ]

  const columns: TableColumnsType<ReturnOpportunityProductReadable> = [
    {
      title: t('generic.name'),
      dataIndex: 'product',
      key: 'product',
      render: (product) => (
        <ConditionalText condition={!!product?.name}>
          {product?.name}
        </ConditionalText>
      )
    },
    {
      title: t('opportunity.price'),
      dataIndex: 'custom_price',
      key: 'custom_price',
      render: (custom_price) => (
        <ConditionalText condition={!!custom_price}>
          {composePrice(custom_price, opportunityCurrency ?? '')}
        </ConditionalText>
      )
    },
    {
      title: (
        <Tooltip title={t('opportunity.unit_of_measure')} placement="top">
          <span>{t('opportunity.uom')}</span>
        </Tooltip>
      ),
      dataIndex: ['uom', 'name'],
      key: 'uom_name',
      render: (uom) => (
        <ConditionalText condition={!!uom}>{uom}</ConditionalText>
      )
    },
    {
      title: t('billing.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => (
        <ConditionalText condition={!!quantity}>{quantity}</ConditionalText>
      )
    },
    {
      title: t('opportunity.discount'),
      dataIndex: 'extra_discount',
      key: 'extra_discount',
      render: (extra_discount) => (
        <ConditionalText condition={!!extra_discount}>
          {`${extra_discount} ${'%'}`}
        </ConditionalText>
      )
    },
    {
      title: t('task.value'),
      key: 'value',
      render: (_, record) => {
        const lineTotal = summaryData?.getLineTotal(record) ?? 0

        return (
          <ConditionalText condition={!!lineTotal}>
            {composePrice(
              String(lineTotal.toFixed(2)),
              record?.currency?.symbol ?? opportunityCurrency ?? ''
            )}
          </ConditionalText>
        )
      }
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at) => <DayLabel date={created_at} />
    },
    {
      title: t('lead.modified_at'),
      dataIndex: 'modified_at',
      key: 'modified_at',
      render: (modified_at) => <DayLabel date={modified_at} />
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, product) => <TableActions options={actionItems(product)} />
    }
  ]

  const actionComponent = useMemo(() => {
    if (!modalState.type) return null

    const Component = modalTypes[modalState.type]

    return (
      <Component
        open
        product={modalState.product ?? undefined}
        opportunityData={opportunityData}
        opportunityProducts={opportunityProducts}
        onCancel={() => setModalState({ type: null, product: null })}
      />
    )
  }, [
    modalState.type,
    modalState.product,
    opportunityData,
    opportunityProducts
  ])

  return (
    <Flex vertical gap={16}>
      <TableContainer
        columns={columns}
        data={opportunityProducts}
        loading={isLoading}
        rightChildren={
          <ProductsHeader
            onAdd={() => setModalState({ type: 'add', product: null })}
          />
        }
        rowKey="id"
        summary={() => {
          if (!summaryData) return null

          return (
            <Table.Summary.Row>
              {[
                <Text weight={500}>{t('reports.total')}</Text>,
                <Text weight={500}>
                  {composePrice(
                    String(summaryData.totalPrice.toFixed(2)),
                    opportunityCurrency ?? ''
                  )}
                </Text>,
                '-',
                '-',
                <Text weight={500}>
                  {calculateDiscount(
                    summaryData.totalPrice.toFixed(2),
                    summaryData.totalAfterDiscount.toFixed(2)
                  )}
                </Text>,
                <Text weight={500}>
                  {composePrice(
                    String(summaryData.totalAfterDiscount.toFixed(2)),
                    opportunityCurrency ?? ''
                  )}
                </Text>,
                null,
                null,
                null
              ].map((content, index) => (
                <Table.Summary.Cell key={index} index={index}>
                  {content}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          )
        }}
      />

      {actionComponent}
    </Flex>
  )
}
